import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle, Bike, CheckCircle2, Clock3, Loader2, Navigation,
  PackageCheck, RefreshCw, Route, Store, Sun,
} from "lucide-react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";

import { createClientRequestId } from "../../utils/clientRequestId";
import RiderAttemptOutcomePanel from "../components/RiderAttemptOutcomePanel";
import RiderExecutionAuthorityCard from "../components/RiderExecutionAuthorityCard";
import RiderExecutionProblemPanel from "../components/RiderExecutionProblemPanel";
import RiderNextPickupNotice from "../components/RiderNextPickupNotice";
import RiderParcelHandoffCard from "../components/RiderParcelHandoffCard";
import RiderCashReturnPanel from "../components/RiderCashReturnPanel";
import RiderParcelReturnPanel from "../components/RiderParcelReturnPanel";
import RiderRemittancePanel from "../components/RiderRemittancePanel";
import RiderRunCompletionCard from "../components/RiderRunCompletionCard";
import DeliveryJourneyShell from "../components/DeliveryJourneyShell";
import DeliveryEssentials from "../components/DeliveryEssentials";
import ParcelSortTag from "../../features/delivery/components/ParcelSortTag";
import PickupJourneyShell from "../components/PickupJourneyShell";
import { buildGoogleMapsNavigationUrl } from "../../features/delivery/maps/navigationUrl";
import { useRiderPortal } from "../context/RiderPortalContext";
import useScreenWakeLock from "../hooks/useScreenWakeLock";
import { riderApi } from "../services/riderApi";
import RiderWithdrawalNotice from "../components/RiderWithdrawalNotice";
import { riderExecutionHeaders } from "../utils/riderExecutionAuthority";
import { pickupMovedBetween, pickupMovedNotice } from "../utils/pickupOriginChange";

const TARGET_AUTO_ADVANCE_MS = 3000;

const RESOLVED = new Set(["ARRIVED", "ATTEMPT_FAILED", "RESCHEDULED", "DELIVERED", "CANCELLED"]);

function label(value) {
  return String(value || "").replaceAll("_", " ").toLowerCase()
    .replace(/^./, (letter) => letter.toUpperCase());
}

function formatDistance(value) {
  if (value == null || value === "") return "Unavailable";
  const meters = Number(value || 0);
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${meters} m`;
}

function formatDuration(value) {
  if (value == null || value === "") return "Unavailable";
  const minutes = Math.max(1, Math.round(Number(value || 0) / 60));
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
}

function captureFreshLocation() {
  if (!navigator.geolocation) {
    return Promise.reject(Object.assign(new Error('Location is unavailable'), { code: 'PICKUP_LOCATION_REQUIRED' }));
  }
  return new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(
    (position) => resolve({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      capturedAt: position.timestamp,
    }),
    reject,
    // A fix up to 10 s old is fresh enough: the server accepts 30 s, and the
    // tracking watcher keeps one current. Demanding a brand-new lock (0) made a
    // Rider indoors wait the full 15 s and then fail.
    { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 },
  ));
}

function runError(error) {
  if (["RUN_CONFLICT", "RUN_VERSION_INVALID", "STOP_NOT_ELIGIBLE", "ARRIVAL_NOT_ELIGIBLE"].includes(error?.code)) {
    return "This run changed while you were working. We refreshed the latest details.";
  }
  if (['PICKUP_CODE_INVALID','PICKUP_AUTHORIZATION_INVALID'].includes(error?.code)) return 'The store authorization expired or the parcel list changed. Ask staff to review and release it again.';
  if (error?.code === 1) return 'Location access is blocked for Apparify. Allow location in your phone or browser settings, then try again.';
  if (error?.code === 2) return 'Your phone could not find your location. Turn on location services, then try again.';
  if (error?.code === 3) return 'Getting your current location took too long. Move near a window or outdoors, then try again.';
  if (error?.code === 'PICKUP_LOCATION_REQUIRED') return 'We could not verify a fresh location for this action. Capture it again and retry.';
  if (error?.code === 'PICKUP_CONFLICT') return 'Your pickup changed. Refresh the trip and review its checklist.';
  // The trip changed after this screen loaded - typically the store moved its
  // pickup point. The page refreshes on its own; this says why the tap failed.
  if (error?.code === 'DELIVERY_STALE_VERSION') return 'This trip changed since you opened it. Check the pickup location shown before you set off.';
  if (error?.code === "DELIVERY_ATTEMPT_LIMIT_REACHED") return "This parcel has reached its delivery attempt limit. The store decides what happens next.";
  if (error?.code === "RIDER_NOT_ON_DUTY") return "Go on duty before starting this run.";
  if (error?.code === "RUN_PICKUP_INCOMPLETE") return "Every parcel must be handed to you before the run can start.";
  if (error?.code === "RIDER_CAPACITY_EXCEEDED") return "The pickup would exceed your parcel capacity. Contact your dispatcher.";
  // The server's own refusals are written for the Rider; show them rather than
  // blaming the connection. Only a missing response is a connection problem.
  if (!error?.status) return "We couldn’t reach the server. Check your connection and try again.";
  if (error.status >= 500) return "Something went wrong on our side. Try again in a moment.";
  if (error?.message && error.message !== "Rider request failed") return error.message;
  return "That action was refused. Refresh the trip and try again.";
}

function StopCard({ stop, next, handingBack = false }) {
  const resolved = RESOLVED.has(stop.status);
  return (
    <article className={`rounded-3xl border p-4 ${next ? "border-emerald-300 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10" : "border-stone-200 bg-white dark:border-white/10 dark:bg-neutral-900"}`}>
      <div className="flex items-start gap-3">
        <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-2xl ${resolved ? "bg-stone-100 text-stone-500 dark:bg-white/10" : "bg-emerald-600 text-white"}`}>
          {resolved ? <CheckCircle2 className="h-5 w-5" aria-hidden="true" /> : <span className="font-semibold tabular-nums">{stop.sequence}</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              {/* The same tag printed large on the parcel, so the rider can find
                  this stop's bag without reading the full code. */}
              <ParcelSortTag labelCode={stop.parcelLabelCode} />
              <p className="truncate font-semibold">{stop.orderReference || `Delivery ${stop.sequence}`}</p>
            </div>
            <span className="shrink-0 rounded-full bg-white/70 px-2.5 py-1 text-xs font-semibold text-stone-600 dark:bg-black/20 dark:text-stone-300">{handingBack ? "Being handed back" : label(stop.status)}</span>
          </div>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">{stop.destinationArea || stop.address}</p>
          <DeliveryEssentials delivery={stop.delivery} deliveryFailed={stop.status === "ATTEMPT_FAILED" || stop.attempt?.status === "FAILED"} />
          {next && <p className="mt-2 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">Next stop</p>}
        </div>
      </div>
    </article>
  );
}

export default function RiderActiveRun() {
  const { businessId, context, refreshContext, currentRun: sharedRun } = useRiderPortal();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  // Which action failed, so its message appears beside the button that was
  // pressed. The page banner is hidden while a journey panel is showing, which
  // is how a refused handoff used to look like nothing happened.
  const [errorAction, setErrorAction] = useState("");
  const [notice, setNotice] = useState("");
  const [, setAuthorityRevision] = useState(0);
  useEffect(()=>{if(sharedRun!==undefined){setRun(sharedRun);setLoading(false);}},[sharedRun]);
  const [confirmAction, setConfirmAction] = useState("");
  const outlet = useOutletContext();
  const navigate = useNavigate();
  const currentPosition = outlet?.currentPosition || null;
  // A rider following guidance leaves the phone in a cradle and does not touch
  // it, so the screen sleeps and the journey vanishes exactly when their hands
  // are busy. Held only while the trip is actually running, and released the
  // moment it is not - never for the whole time the app is open.
  const runIsLive = ["READY", "ACTIVE", "INTERRUPTED"].includes(run?.status)
    || (run?.status === "PLANNED" && Boolean(run?.pickupStartedAt));
  const wakeLock = useScreenWakeLock(runIsLive);
  const keys = useRef(new Map());
  const loadSequence = useRef(0);
  const autoAdvanceTimer = useRef(null);

  const load = useCallback(async ({ quiet = false, preserveError = false } = {}) => {
    const sequence = ++loadSequence.current;
    if (!quiet) setLoading(true);
    if (!preserveError) setError("");
    try {
      const next = await riderApi.getCurrentRun(businessId);
      if (sequence !== loadSequence.current) return;
      setRun((current) => {
        // A moved pickup point is announced first and on its own: it also
        // re-plans the route, and the route message would describe "go
        // somewhere else" as a reordering of stops.
        const moved = pickupMovedNotice(pickupMovedBetween(current, next));
        if (moved) {
          setNotice(moved);
        } else if (current?.route?.versionNo && next?.route?.versionNo > current.route.versionNo) {
          setNotice("Your route was updated. The latest stop order is shown below.");
        }
        return next;
      });
    } catch {
      if (sequence !== loadSequence.current) return;
      setError("We couldn’t load your current run. Check your connection and try again.");
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    // A background refresh must not wipe what the Rider was just told: a
    // refused action's message used to vanish within five seconds.
    const poll = () => { if (document.visibilityState === 'visible') load({ quiet: true, preserveError: true }); };
    const interval = window.setInterval(poll, 5000);
    const visibility = () => { if (document.visibilityState === 'visible') poll(); };
    window.addEventListener('focus', poll);
    document.addEventListener('visibilitychange', visibility);
    return () => { window.clearInterval(interval); window.removeEventListener('focus', poll); document.removeEventListener('visibilitychange', visibility); };
  }, [load]);

  useEffect(() => {
    load();
    return () => {
      loadSequence.current += 1;
      if (autoAdvanceTimer.current) window.clearTimeout(autoAdvanceTimer.current);
    };
  }, [load]);

  // WP7 target-v2: what the Rider is still physically carrying, and the
  // hand-over waiting at the counter, if any (loaded by its own effect below).
  const [heldParcels, setHeldParcels] = useState([]);
  const [returnSession, setReturnSession] = useState(null);
  const handover = returnSession?.status === "OPEN" ? returnSession : null;
  // "I cannot continue": the whole trip waits for the counter.
  const emergencyWaiting = handover?.sessionKind === "EMERGENCY_RECOVERY";
  const presentedKey = (handover?.expectedShipmentIds || []).join(",");
  // A parcel returned to the store, or presented for hand-over, is not the
  // Rider's to deliver now; the server refuses it too
  // (DELIVERY_PARCEL_PRESENTED_FOR_RETURN) until the hand-over is withdrawn.
  const stillCarried = useCallback((stop) => stop.assignmentStatus !== "CANCELLED"
    && !presentedKey.split(",").includes(String(stop.shipmentId)), [presentedKey]);
  const nextStop = useMemo(() => run?.stops?.find((stop) => ["QUEUED", "ACTIVE"].includes(stop.status) && stillCarried(stop)) || null, [run, stillCarried]);
  const activeStop = useMemo(() => run?.stops?.find((stop) => stop.status === "ACTIVE" && stillCarried(stop)) || null, [run, stillCarried]);
  const arrivedStop = useMemo(() => run?.stops?.find((stop) => stop.status === "ARRIVED" && stop.attempt?.status === "ARRIVED" && stillCarried(stop)) || null, [run, stillCarried]);
  // Parcels the Rider still has a delivery to make for. While any remain, the
  // return and finish-trip panels are exceptions, not the next step.
  // The Rider handed the trip's cash over early because the trip cannot go on.
  // Deliveries on it are closed (the server refuses them too,
  // DELIVERY_TRIP_STOPPED_BY_EMERGENCY); what is left is returning the parcels.
  const tripStopped = Number(run?.workflowVersion) === 2 && Boolean(run?.stoppedByEmergencyCash);
  const deliveryPending = useMemo(() => !tripStopped && Boolean(run?.stops?.some((stop) => (
    ["QUEUED", "ACTIVE", "ARRIVED"].includes(stop.status) && stop.assignmentStatus === "PICKED_UP"
  ))), [run, tripStopped]);
  // WP6: a failed stop whose COD cash the Rider is still holding. The money does
  // not disappear because the delivery did, so it stays on screen until it is
  // either returned and confirmed, or handed to the business.
  const cashReturnStop = useMemo(() => run?.stops?.find((stop) => (
    stop.attempt?.status === "FAILED"
    && stop.cod?.status === "COLLECTED"
    && Number(stop.cod?.collectedAmountCents || 0) > 0
  )) || null, [run]);
  const [cashReturns, setCashReturns] = useState([]);

  useEffect(() => {
    if (!businessId || Number(run?.workflowVersion) !== 2 || !cashReturnStop) {
      setCashReturns([]);
      return undefined;
    }
    const controller = new AbortController();
    riderApi.getCashReturns(businessId, { signal: controller.signal })
      .then((result) => setCashReturns(Array.isArray(result?.items) ? result.items : []))
      // A failed lookup must not hide the panel: the Rider still needs to be able
      // to report the return.
      .catch(() => setCashReturns([]));
    return () => controller.abort();
  }, [businessId, cashReturnStop, run?.workflowVersion, run?.version]);
  // WP7 target-v2: what the Rider is still physically carrying, and whether they
  // have already presented any of it at the counter. Its own effect, because it
  // has nothing to do with the run poll and must not delay it.

  useEffect(() => {
    if (!businessId || Number(run?.workflowVersion) !== 2) {
      setHeldParcels([]);
      setReturnSession(null);
      return undefined;
    }
    const controller = new AbortController();
    riderApi.getHeldParcels(businessId, { signal: controller.signal })
      .then((result) => {
        setHeldParcels(Array.isArray(result?.items) ? result.items : []);
        setReturnSession(result?.openSession || null);
      })
      .catch(() => {
        setHeldParcels([]);
        setReturnSession(null);
      });
    return () => controller.abort();
  }, [businessId, run?.workflowVersion, run?.version]);

  const awaitingPickup = useMemo(() => run?.stops?.filter((stop) => stop.assignmentStatus === "COMMITTED") || [], [run]);
  const executionHeaders = Number(run?.workflowVersion) === 2
    ? riderExecutionHeaders(businessId, run?.id)
    : null;
  const executionEnabled = Number(run?.workflowVersion) !== 2 || Boolean(executionHeaders);
  const actionKey = (identity) => {
    if (!keys.current.has(identity)) keys.current.set(identity, createClientRequestId(identity));
    return keys.current.get(identity);
  };

  const act = async (identity, request) => {
    if (!run || busy) return;
    setBusy(identity);
    setError("");
    setNotice("");
    try {
      const result = await request(actionKey(identity));
      keys.current.delete(identity);
      if (result?.run) setRun(result.run);
      else await load({ quiet: true });
      await refreshContext();
      setConfirmAction("");
      return true;
    } catch (requestError) {
      setError(runError(requestError));
      setErrorAction(identity);
      if (["RUN_CONFLICT", "STOP_NOT_ELIGIBLE", "ARRIVAL_NOT_ELIGIBLE"].includes(requestError?.code)) {
        await load({ quiet: true, preserveError: true });
      }
      return false;
    } finally {
      setBusy("");
    }
  };

  // One tap: nothing physical happens between starting a stop and starting its
  // delivery attempt, so the Rider is not asked twice. Still two server commands;
  // if the second is refused, the started stop is shown and "Begin delivery
  // attempt" remains as the retry.
  const goToStop = () => act(`activate_${nextStop.id}`, async (idempotencyKey) => {
    const headers = Number(run.workflowVersion) === 2 ? [{ executionHeaders }] : [];
    const activated = await riderApi.activateStop(businessId, nextStop.id, { expectedVersion: nextStop.version, expectedRunVersion: run.version, idempotencyKey }, ...headers);
    const started = activated?.run?.stops?.find((stop) => Number(stop.id) === Number(nextStop.id));
    if (!started || started.status !== "ACTIVE" || started.attempt) return activated;
    const attemptIdentity = `attempt_${started.id}`;
    try {
      await riderApi.startAttempt(businessId, started.id, { expectedVersion: started.version, idempotencyKey: actionKey(attemptIdentity) }, ...headers);
    } catch (attemptError) {
      if (activated?.run) setRun(activated.run);
      throw attemptError;
    }
    keys.current.delete(attemptIdentity);
    return {};
  });

  const outcomeUpdated = async (result, message) => {
    const nextRunHasDeliveries = Boolean(result?.run?.stops?.some((stop) => (
      ["QUEUED", "ACTIVE", "ARRIVED"].includes(stop.status) && stop.assignmentStatus === "PICKED_UP"
    )));
    // The last delivery: there is no next stop to move to. The trip stays open
    // until the Rider hands over what they carry and finishes it.
    if (Number(run?.workflowVersion) === 2 && result?.delivered === true && result?.run?.status === "ACTIVE" && !nextRunHasDeliveries) {
      setRun(result.run);
      setError("");
      setNotice(`${message} That was your last delivery. Ride back to the store to hand over what you carry, then finish the trip.`);
      await refreshContext();
      return;
    }
    if (Number(run?.workflowVersion) === 2 && result?.delivered === true && result?.run?.status === "ACTIVE") {
      setError("");
      setNotice(`${message} Moving to the next stop in 3 seconds.`);
      if (autoAdvanceTimer.current) window.clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = window.setTimeout(async () => {
        setRun(result.run);
        setNotice("Next stop is ready. Review the destination before starting it.");
        await refreshContext();
      }, TARGET_AUTO_ADVANCE_MS);
      return;
    }
    if (result?.run) setRun(result.run);
    else await load({ quiet: true });
    setError("");
    setNotice(message);
    await refreshContext();
  };

  if (loading) {
    return <div className="grid min-h-[55vh] place-items-center"><div className="text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-emerald-600" /><p className="mt-3 text-sm text-stone-500">Loading your run…</p></div></div>;
  }

  if (error && !run) {
    return (
      <section role="alert" className="rounded-3xl border border-red-200 bg-red-50 p-6 text-red-800 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
        <AlertCircle className="h-7 w-7" aria-hidden="true" />
        <h1 className="mt-4 text-pretty text-xl font-semibold">Couldn’t load your current run</h1>
        <p className="mt-2 text-sm">Check your connection and try again.</p>
        <button type="button" onClick={() => load()} className="mt-5 min-h-12 touch-manipulation rounded-2xl bg-red-700 px-5 font-semibold text-white hover:bg-red-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2 dark:bg-red-500 dark:text-stone-950">
          Try again
        </button>
      </section>
    );
  }

  if (!run) {
    return (
      <>
      {/* The trip that just disappeared, and why. */}
      <RiderWithdrawalNotice withdrawals={context?.duty?.recentWithdrawals} className="mb-4" />
      <section className="rounded-3xl border border-dashed border-stone-300 p-8 text-center dark:border-white/15">
        <Bike className="mx-auto h-8 w-8 text-stone-400" aria-hidden="true" />
        <h1 className="mt-4 text-xl font-semibold">{Number(context?.duty?.committedLoad) > 0 ? 'Preparing your pickup trip' : 'No deliveries in progress'}</h1>
        <p className="mt-2 text-sm text-stone-500 dark:text-stone-400">{['UNAVAILABLE','UNKNOWN'].includes(context?.duty?.workflowHealth?.state) && Number(context?.duty?.committedLoad)>0 ? 'Your parcels are reserved, but the route worker is not reporting healthy. Ask your store to restart delivery processing.' : context?.duty?.routePreparation?.status === 'RETRY' ? 'Route preparation was interrupted. We are retrying automatically and your parcels remain reserved. Contact your store if this continues.' : Number(context?.duty?.committedLoad) > 0 ? 'Your committed parcels are reserved while we prepare the pickup route. You can review them below.' : 'Work your store commits to you will appear here.'}</p>
        <Link to="/rider/assignments" className="mt-5 inline-flex min-h-12 touch-manipulation items-center rounded-2xl bg-emerald-600 px-5 font-semibold text-white hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2">View assignments</Link>
      </section>
      </>
    );
  }

  const canPickup = run.status === "PLANNED" && awaitingPickup.length > 0;
  const hasJourney = canPickup || ["READY", "ACTIVE"].includes(run.status);

  return (
    <div className="space-y-5 [&_button]:touch-manipulation [&_button]:focus-visible:outline-none [&_button]:focus-visible:ring-2 [&_button]:focus-visible:ring-emerald-500 [&_button]:focus-visible:ring-offset-2 dark:[&_button]:focus-visible:ring-offset-neutral-950" aria-busy={Boolean(busy)}>
      <section className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">My deliveries</p>
          <h1 className="mt-1 text-pretty text-2xl font-semibold tracking-tight">{run.status === "PLANNED" ? (run.pickupStartedAt ? "Collect your parcels" : "Prepare for pickup") : label(run.status)}</h1>
          <p className="mt-1 flex items-center gap-2 text-sm text-stone-600 dark:text-stone-400"><Store className="h-4 w-4" /> {run.businessName || context?.business?.name}</p>
          {/* Quiet, not celebratory: the rider only needs to know why the phone
              stopped sleeping, so it never becomes a mystery. */}
          {wakeLock.held && <p className="mt-1 flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400"><Sun className="h-3.5 w-3.5" aria-hidden="true" /> Screen stays on while this trip is running</p>}
        </div>
        <button type="button" onClick={() => load({ quiet: true })} disabled={Boolean(busy)} className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-stone-200 bg-white hover:bg-stone-100 disabled:opacity-50 dark:border-white/10 dark:bg-neutral-900 dark:hover:bg-white/10" aria-label="Refresh active run"><RefreshCw className="h-4 w-4" aria-hidden="true" /></button>
      </section>

      {notice && <p role="status" aria-live="polite" className="rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-800 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-200">{notice}</p>}
      {error && !hasJourney && <div role="alert" className="flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300"><AlertCircle className="h-5 w-5 shrink-0" /><div><p>{error}</p><button type="button" onClick={() => load()} className="mt-2 font-semibold underline">Refresh run</button></div></div>}

      {Number(run.workflowVersion) === 2 && ["READY", "ACTIVE"].includes(run.status) && (
        <RiderNextPickupNotice businessId={businessId} committedLoad={context?.duty?.committedLoad} refreshKey={run.version} />
      )}
      {Number(run.workflowVersion) === 2 && <RiderExecutionAuthorityCard businessId={businessId} run={run} onChanged={() => setAuthorityRevision((value) => value + 1)} />}

      {/* WP4 target-v2: two-party handoff. Dark for v1 runs. */}
      {/* The handoff card belongs to pickup; after deliveries start it is noise. */}
      {Number(run.workflowVersion) === 2 && run.status !== "ACTIVE" && (
        <RiderParcelHandoffCard
          run={run}
          busy={Boolean(busy)}
          onVerifyChallenge={(input) => act('handoff_verify', idempotencyKey => riderApi.verifyHandoffChallenge(
            businessId, run.id, { ...input, idempotencyKey },
          ))}
          actionError={errorAction === 'handoff_verify' ? error : ""}
        />
      )}


      {canPickup && (
        <PickupJourneyShell
          run={run}
          currentPosition={currentPosition}
          dutyState={context?.duty?.dutyState}
          awaitingPickup={awaitingPickup}
          busy={Boolean(busy)}
          actionError={errorAction === 'handoff_verify' ? "" : error}
          executionEnabled={executionEnabled}
          onRefresh={() => load({ quiet: true })}
          onStart={async () => {
            const started = await act('depart_pickup', async (idempotencyKey) => riderApi.departPickup(businessId, run.id, {
              expectedVersion: run.version,
              idempotencyKey,
              location: await captureFreshLocation(),
            }));
            if (started) setNotice('Pickup journey started. Travel to the store and record your arrival there.');
          }}
          onArrive={() => act('arrive_pickup', async (idempotencyKey) => riderApi.arrivePickup(businessId, run.id, {
            expectedVersion: run.version,
            idempotencyKey,
            location: await captureFreshLocation(),
          }))}
        />
      )}

      {/* Once nothing is left to deliver there is no journey step to show; the
          trip cash and Finish-trip panels below are the next thing to do. */}
      {emergencyWaiting && (
        <section className="rounded-3xl border border-sky-200 bg-sky-50 p-5 text-sky-950 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-100" data-testid="handover-waiting">
          <h2 className="flex items-center gap-2 font-semibold"><Store className="h-5 w-5" aria-hidden="true" /> Waiting for the store to take the parcels back</h2>
          <p className="mt-1 text-sm">Hand them over at the counter; staff scan each one. Your deliveries are paused until they do.</p>
          {run.pickupOrigin?.address && <p className="mt-2 text-sm font-medium">{run.pickupOrigin.address}</p>}
          {run.pickupOrigin && buildGoogleMapsNavigationUrl(run.pickupOrigin) && (
            <a href={buildGoogleMapsNavigationUrl(run.pickupOrigin)} target="_blank" rel="noreferrer" className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl bg-sky-700 px-4 text-sm font-semibold text-white hover:bg-sky-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500">
              <Navigation className="h-4 w-4" aria-hidden="true" /> Open in Google Maps
            </a>
          )}
          <p className="mt-3 text-sm">Changed your mind? Withdraw the hand-over below to continue your trip.</p>
        </section>
      )}

      {!emergencyWaiting && !tripStopped && (run.status === "READY" || (run.status === "ACTIVE" && (nextStop || activeStop || arrivedStop))) && (
        <DeliveryJourneyShell
          run={run}
          nextStop={nextStop}
          activeStop={activeStop}
          arrivedStop={arrivedStop}
          currentPosition={currentPosition}
          busy={Boolean(busy)}
          dutyState={context?.duty?.dutyState}
          actionError={errorAction === 'handoff_verify' ? "" : error}
          onRefresh={() => load({ quiet: true })}
          confirmingArrival={confirmAction === "arrive"}
          onConfirmingArrival={(value) => setConfirmAction(value ? "arrive" : "")}
          onStartRun={() => act("run_start", (idempotencyKey) => riderApi.startRun(businessId, run.id, { expectedVersion: run.version, idempotencyKey }, ...(Number(run.workflowVersion) === 2 ? [{ executionHeaders }] : [])))}
          onActivateStop={goToStop}
          onStartAttempt={() => act(`attempt_${activeStop.id}`, (idempotencyKey) => riderApi.startAttempt(businessId, activeStop.id, { expectedVersion: activeStop.version, idempotencyKey }, ...(Number(run.workflowVersion) === 2 ? [{ executionHeaders }] : [])))}
          onArrive={() => act(`arrive_${activeStop.id}`, (idempotencyKey) => riderApi.arriveStop(businessId, activeStop.id, { attemptId: activeStop.attempt.id, expectedVersion: activeStop.version, expectedAttemptVersion: activeStop.attempt.version, expectedRunVersion: run.version, idempotencyKey }, ...(Number(run.workflowVersion) === 2 ? [{ executionHeaders }] : [])))}
          outcome={arrivedStop ? <RiderAttemptOutcomePanel businessId={businessId} run={run} stop={arrivedStop} executionHeaders={executionHeaders} executionEnabled={executionEnabled} onUpdated={outcomeUpdated} /> : null}
        />
      )}

      {/* WP6 target-v2: cash still held after a failed COD stop. Dark for v1 runs. */}
      {Number(run.workflowVersion) === 2 && cashReturnStop && (
        <RiderCashReturnPanel
          businessId={businessId}
          stop={cashReturnStop}
          cashReturn={cashReturns.find(
            (entry) => Number(entry.deliveryAttemptId) === Number(cashReturnStop.attempt?.id),
          ) || null}
          disabled={Boolean(busy) || !executionEnabled}
          onChanged={(result) => {
            setNotice(result?.obligationCleared
              ? "The customer confirmed the cash return. It is no longer counted against you."
              : "Cash return reported. It only clears once the customer confirms it.");
            load({ quiet: true });
          }}
        />
      )}

      {/* After the journey: what closes the trip, then exception reporting. */}
      {/* End of the trip, in the order it happens physically: ride back to the
          store, hand over the cash, return anything undelivered, finish. The cash
          is handed over once, here - never between stops (2026-09-26). */}
      {Number(run.workflowVersion) === 2 && run.status === "ACTIVE" && !deliveryPending && run.pickupOrigin && (
        <section className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-950 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-100" data-testid="ride-back">
          <h2 className="flex items-center gap-2 font-semibold"><Store className="h-5 w-5" aria-hidden="true" /> {tripStopped ? "Deliveries on this trip are closed" : "Deliveries done. Ride back to the store"}</h2>
          {tripStopped && <p className="mt-1 text-sm">You handed over the trip&apos;s cash because you can&apos;t finish it. Don&apos;t deliver or collect cash for the parcels you still have: take them back to the store.</p>}
          <p className="mt-1 text-sm">{run.pickupOrigin.address || run.businessName || "Your store"}</p>
          <p className="mt-1 text-sm">{tripStopped
            ? `There, ${heldParcels.length ? "return the parcels you still have and " : ""}finish the trip.`
            : `There, hand over the cash${heldParcels.length ? ", return the parcels you still have" : ""} and finish the trip.`}</p>
          {buildGoogleMapsNavigationUrl(run.pickupOrigin) && (
            <a href={buildGoogleMapsNavigationUrl(run.pickupOrigin)} target="_blank" rel="noreferrer" className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl bg-emerald-700 px-4 text-sm font-semibold text-white hover:bg-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
              <Navigation className="h-4 w-4" aria-hidden="true" /> Open in Google Maps
            </a>
          )}
        </section>
      )}
      {/* WP8 target-v2: the trip's cash, handed to the counter. Same boundary as
          the parcel panel above - presenting is not settling. Dark for v1 runs. */}
      {Number(run.workflowVersion) === 2 && (
        <RiderRemittancePanel
          businessId={businessId}
          runId={run.id}
          runVersion={run.version}
          deliveriesRemaining={deliveryPending}
          disabled={Boolean(busy) || !executionEnabled}
          onChanged={() => {
            setNotice("Presented for counting. Staff confirm the amount they physically count.");
            load({ quiet: true });
          }}
        />
      )}
      {/* WP7 target-v2: Parcels still in the Rider's hands, and the two-party
          handover that is the only way they leave. While there is still a
          delivery to make it is an exception path, so it stays folded away:
          it used to sit above the journey and tell a Rider who had just
          collected a parcel to return it. Dark for v1 runs. */}
      {Number(run.workflowVersion) === 2 && (heldParcels.length > 0 || returnSession) && (
        deliveryPending && !returnSession ? (
          <details className="rounded-3xl border border-stone-200 bg-white p-5 dark:border-white/10 dark:bg-neutral-900" data-testid="return-exception">
            <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
              Can&apos;t finish this trip? Return parcels to the store
            </summary>
            <div className="mt-3">
              <RiderParcelReturnPanel
                businessId={businessId}
                heldParcels={heldParcels}
                openSession={returnSession}
                disabled={Boolean(busy) || !executionEnabled}
                onChanged={(result) => {
                  setReturnSession(result?.session?.status === "OPEN" ? result.session : null);
                  setNotice(result?.session?.status === "ABANDONED"
                    ? "Withdrawn. Those parcels are still in your custody."
                    : "Presented at the counter. Staff confirm each parcel when they take it.");
                  load({ quiet: true });
                }}
              />
            </div>
          </details>
        ) : (
        <RiderParcelReturnPanel
          businessId={businessId}
          heldParcels={heldParcels}
          openSession={returnSession}
          initialMode={tripStopped ? "emergency" : "return"}
          disabled={Boolean(busy) || !executionEnabled}
          onChanged={(result) => {
            setReturnSession(result?.session?.status === "OPEN" ? result.session : null);
            setNotice(result?.session?.status === "ABANDONED"
              ? "Withdrawn. Those parcels are still in your custody."
              : "Presented at the counter. Staff confirm each parcel when they take it.");
            load({ quiet: true });
          }}
        />
        )
      )}
      {/* Finishing is the last step, so it appears once nothing is left to deliver. */}
      {Number(run.workflowVersion) === 2 && run.status === "ACTIVE" && !deliveryPending && (
        <RiderRunCompletionCard
          businessId={businessId}
          run={run}
          executionEnabled={executionEnabled}
          executionHeaders={executionHeaders}
          onRefresh={() => load({ quiet: true, preserveError: true })}
          onCompleted={async () => {
            setRun(null);
            await refreshContext();
            navigate('/rider/history', { replace: true });
          }}
        />
      )}
      {Number(run.workflowVersion) === 2 && ["READY", "ACTIVE"].includes(run.status) && (
        <RiderExecutionProblemPanel
          businessId={businessId}
          run={run}
          stop={arrivedStop || activeStop || nextStop}
          executionHeaders={executionHeaders}
          onReported={() => setNotice("Problem saved with this trip. Your deliveries are unchanged; call the store if you need help now.")}
        />
      )}

      <section className="rounded-3xl bg-stone-900 p-5 text-white shadow-sm dark:bg-white dark:text-stone-950">
        <div className="grid grid-cols-3 gap-3 text-center">
          <div><PackageCheck className="mx-auto h-5 w-5 text-emerald-400 dark:text-emerald-600" aria-hidden="true" /><p className="mt-2 text-lg font-semibold tabular-nums">{run.load?.pickedUp || 0}</p><p className="text-xs opacity-70">In custody</p></div>
          <div><Route className="mx-auto h-5 w-5 text-emerald-400 dark:text-emerald-600" aria-hidden="true" /><p className="mt-2 text-lg font-semibold tabular-nums">{run.load?.resolved || 0}/{run.load?.planned || 0}</p><p className="text-xs opacity-70">Stops</p></div>
          <div><Clock3 className="mx-auto h-5 w-5 text-emerald-400 dark:text-emerald-600" aria-hidden="true" /><p className="mt-2 text-lg font-semibold tabular-nums">{formatDuration(run.route?.durationS)}</p><p className="text-xs opacity-70">Planned travel</p></div>
        </div>
        <div className="mt-4 flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3 text-sm dark:bg-stone-900/10"><span>Planned road distance</span><strong>{formatDistance(run.route?.distanceM)}</strong></div>
      </section>
      <section><h2 className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-stone-500">Route stops</h2><div className="space-y-3">{run.stops.map((stop) => <StopCard key={stop.id} stop={stop} next={run.status !== "PLANNED" && stop.id === nextStop?.id} handingBack={Boolean(handover) && stop.assignmentStatus !== "CANCELLED" && !stillCarried(stop)} />)}</div></section>

      <section className="rounded-3xl border border-stone-200 bg-white p-5 text-sm dark:border-white/10 dark:bg-neutral-900"><h2 className="font-semibold">Need help with a parcel?</h2><p className="mt-1 text-stone-600 dark:text-stone-400">If you have collected it, keep it in your custody and contact your dispatcher. Don’t hand it to another rider or mark it complete.</p></section>
    </div>
  );
}
